import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { authApi } from '../../lib/api';
import { Heart, ArrowLeft, Loader2, CheckCircle, AlertTriangle, XCircle } from 'lucide-react';
import { Button } from '../ui/button';

export default function EmailVerifyPage() {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const navigate = useNavigate();

    const [status, setStatus] = useState('loading'); // loading, success, expired, error
    const [message, setMessage] = useState('');

    useEffect(() => {
        if (token) {
            verifyToken();
        } else {
            setStatus('error');
            setMessage('Kein Bestätigungstoken vorhanden.');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const verifyToken = async () => {
        try {
            const res = await authApi.verifyEmail(token);
            const { token: authToken, user } = res.data;
            localStorage.setItem('bgpr_token', authToken);
            localStorage.setItem('bgpr_user', JSON.stringify(user));
            setStatus('success');
            setMessage(res.data.message);
        } catch (error) {
            const code = error.response?.status;
            const detail = error.response?.data?.detail || 'Bestätigung fehlgeschlagen';
            if (code === 410) {
                setStatus('expired');
            } else {
                setStatus('error');
            }
            setMessage(detail);
        }
    };

    const handleGoToProfile = () => {
        window.location.href = '/portal';
    };

    return (
        <div className="min-h-screen bg-gray-50">
            <header className="bg-secondary">
                <div className="max-w-md mx-auto px-4 py-4">
                    <div className="flex items-center justify-between">
                        <Link to="/seminare" className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors text-sm">
                            <ArrowLeft className="h-4 w-4" />
                            Zurück
                        </Link>
                        <div className="flex items-center gap-2">
                            <Heart className="h-5 w-5 text-primary" />
                            <span className="text-sm font-semibold text-white">BGPR</span>
                        </div>
                    </div>
                </div>
            </header>

            <div className="flex items-center justify-center p-4 pt-16">
                <div className="w-full max-w-md bg-white rounded-2xl shadow-sm border border-gray-100 p-8 text-center">
                    {status === 'loading' && (
                        <div data-testid="verify-loading">
                            <Loader2 className="h-12 w-12 mx-auto mb-4 text-primary animate-spin" />
                            <h2 className="text-xl font-bold text-gray-900 mb-2">E-Mail wird bestätigt...</h2>
                            <p className="text-sm text-gray-500">Bitte warten Sie einen Moment.</p>
                        </div>
                    )}

                    {status === 'success' && (
                        <div data-testid="verify-success">
                            <div className="mx-auto mb-4 p-3 bg-green-100 rounded-xl w-fit">
                                <CheckCircle className="h-10 w-10 text-green-600" />
                            </div>
                            <h2 className="text-xl font-bold text-gray-900 mb-2">E-Mail bestätigt!</h2>
                            <p className="text-sm text-gray-500 mb-6">{message}</p>
                            <p className="text-sm text-gray-600 mb-6">
                                Bitte vervollständigen Sie jetzt Ihr Profil, um Lehrgangsbescheinigungen und Lizenzen erhalten zu können.
                            </p>
                            <Button
                                onClick={handleGoToProfile}
                                className="bg-primary hover:bg-primary/80 h-11 px-8"
                                data-testid="go-to-profile"
                            >
                                Profil vervollständigen
                            </Button>
                        </div>
                    )}

                    {status === 'expired' && (
                        <div data-testid="verify-expired">
                            <div className="mx-auto mb-4 p-3 bg-amber-100 rounded-xl w-fit">
                                <AlertTriangle className="h-10 w-10 text-amber-600" />
                            </div>
                            <h2 className="text-xl font-bold text-gray-900 mb-2">Link abgelaufen</h2>
                            <p className="text-sm text-gray-500 mb-6">{message}</p>
                            <Link
                                to="/teilnehmer/registrieren"
                                className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary text-white rounded-xl hover:bg-primary/80 transition-colors font-medium text-sm"
                                data-testid="register-again"
                            >
                                Erneut registrieren
                            </Link>
                        </div>
                    )}

                    {status === 'error' && (
                        <div data-testid="verify-error">
                            <div className="mx-auto mb-4 p-3 bg-red-100 rounded-xl w-fit">
                                <XCircle className="h-10 w-10 text-red-600" />
                            </div>
                            <h2 className="text-xl font-bold text-gray-900 mb-2">Fehler</h2>
                            <p className="text-sm text-gray-500 mb-6">{message}</p>
                            <Link
                                to="/teilnehmer/login"
                                className="text-primary hover:underline font-medium text-sm"
                                data-testid="go-to-login"
                            >
                                Zum Login
                            </Link>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
